import {useLocation, useNavigate} from "react-router-dom";
import {useForm} from "react-hook-form";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {listService} from "../services/listsService";
import {customerService} from "../services/customerService";
import FormSelectCustom from "../shared/components/FormSelectCustom";
import NewEditTruckModal from "../components/customer-management/NewEditTruckModal";
import LinkCCModal from "../components/customer-management/LinkCCModal";
import notificationService from "../services/NotificationService";
import eventEmitter from "../shared/eventEmitter";
import {EventEmitterTypes} from "../shared/eventEmitterEnum";
import {businessYearService} from "../services/businessYearService";
import {AvailableDatePicker} from "../shared/components/AvailableDatePicker";
import {round5} from "../shared/sharedFunctions";
import {useLoading} from "../shared/components/LoadingContext";
import ConfirmationModal from "../components/ConfirmationModal";
import {PopoverButton} from "../shared/components/PopoverButton";


function useURLOrStateVars() {
    const location = useLocation();

    // get state if present
    const s = (location.state || {}) as any
    if (s.customerId && s.jobId) {
        return {
            customerId: s.customerId,
            jobId: s.jobId,
            propertyAddress: s.propertyAddress,
            fromJobDetails: s.fromJobDetails ?? false,
            customerName: s.customerName,
            propertyType: s.propertyType ?? null,
        };
    }

    // if opened in new tab (from estimate details page only rn) use this
    const params = new URLSearchParams(location.search);
    const customerId = params.get("customerId") || undefined;
    const jobId = params.get("jobId") || undefined;

    if (!customerId || !jobId) return null;

    return {
        customerId,
        jobId,
        customerName: params.get("customerName") || '',
        propertyAddress: params.get("propertyAddress") || undefined,
        fromJobDetails: params.get("fromJobDetails") === "true",
        propertyType: params.get("propertyType") || null,
    };
}

const JobDetailsPage: React.FC = () => {
    const { setIsLoading } = useLoading();
    const navigate = useNavigate();
    const state = useURLOrStateVars();
    const { register, control, setValue, reset, getValues, handleSubmit, watch, formState: { dirtyFields, errors } } = useForm();
    const dirtyServiceStatusCard = dirtyFields.jobStatusId;
    const dirtyServiceOtherCard = dirtyFields.dateDone || dirtyFields.serviceTime || dirtyFields.serviceStart || dirtyFields.serviceEnd;
    const dirtyTransactionCard = 
        dirtyFields.cardTransactionDate || dirtyFields.cardAmount || dirtyFields.cardTransactionNumber || dirtyFields.cardType || dirtyFields.cardLast4 || dirtyFields.cardExpirationYear ||
        dirtyFields.depositSlipDate || dirtyFields.depositAmount || dirtyFields.depositPaymentId ||dirtyFields.depositSlipNumber || dirtyFields.depositCheckNumber;
    const dirtyPricingCard = dirtyFields.sealcoatingPrice || dirtyFields.patchworkPrice || dirtyFields.priceAutoAdjustment || dirtyFields.priceManualAdjustment;
    const dirtyEstimateCard = dirtyFields.whenPriceApproved || dirtyFields.priceEstimate;
    const dirtyTrucksCard = dirtyFields.truckComments;
    const dirtyCommentsCard = dirtyFields.officeComments || dirtyFields.webComments || dirtyFields.persistentOfficeComments;

    const [jobTrucks, setJobTrucks] = useState([]);
    const [cardTypes, setCardTypes] = useState();
    const [priceBrackets, setPriceBrackets] = useState([]);
    const [availableDates, setAvailableDates] = useState([]);
    const [allowEdit, setAllowEdit] = useState(false);
    const [showConfimationModalInvoice, setShowConfirmationInvoiceModal] = useState(false);
    const [jobStatuses, setJobStatuses] = useState([]);
    const [originalManualAdj, setOriginalManualAdj] = useState(0);
    const [autoAdj, manualAdj, totalJobPrice] = watch(['autoAdjustmentsAmount', 'manualAdjustmentAmount', 'totalJobPrice']);
    
    const [
        sealcoatingPrice,
        patchworkPrice,
        hotPatchworkPrice,
        sawCutPrice,
        stripingPrice,
        crackSealPrice,
        hotCrackSealPrice,
    ] = watch([
        'sealcoatingPrice',
        'patchworkPrice',
        'hotPatchworkPrice',
        'sawCutPrice',
        'stripingPrice',
        'crackSealPrice',
        'hotCrackSealPrice',
    ]);
    const [dateDone, jobStatusId, jobStatusName, jobEstimate, cardMonth, cardYear, jobSquareFeet] = watch([
        'dateDone',
        'jobStatusId',
        'jobStatusName',
        'jobEstimate',
        'cardExpirationMonth',
        'cardExpirationYear',
        'jobSquareFeet',
    ]);

    const { jobId, customerId } = state || {};

    const estimateTotal = round5(
        Math.ceil((Number(sealcoatingPrice) || 0) +
            (Number(patchworkPrice) || 0) +
            (Number(hotPatchworkPrice) || 0) +
            (Number(sawCutPrice) || 0) +
            (Number(stripingPrice) || 0) +
            (Number(crackSealPrice) || 0) +
            (Number(hotCrackSealPrice) || 0)));

    const total = estimateTotal + (Number(manualAdj) || 0) + (Number(autoAdj) || 0);
    useEffect(() => {
        async function fetchOptions() {
            if(state.jobId){
                try {
                    setIsLoading(true)
                    const [
                        cardTypesRes,
                        jobStatusesRes,
                        jobTrucksRes,
                        jobDataRes,
                        currentYear,
                        availableDates,
                    ] = await Promise.all([
                        listService.getCardTypes(),
                        listService.getJobStatuses(),
                        customerService.getTrucksByJobId(state.customerId, state.jobId),
                        customerService.getCustomerJobByJobId(state.customerId, state.jobId),
                        businessYearService.getCurrentBusinessYear(),
                        customerService.getAvailableJobDates(state.customerId, state.jobId)
                    ]);
                    if (jobDataRes.businessYear === currentYear.year)
                        setAllowEdit(true)
                    setJobTrucks(jobTrucksRes);
                    setAvailableDates(availableDates?.availableDates);
                    setJobStatuses(jobStatusesRes);
                    setCardTypes(cardTypesRes);
                    jobDataRes.whenPriceApproved = jobDataRes.whenPriceApproved?.slice(0, 10)
                    reset(jobDataRes);
                    if (originalManualAdj === 0)
                        setOriginalManualAdj(jobDataRes.manualAdjustmentAmount)
                    const priceBracketsRes = await Promise.resolve(businessYearService.getPriceBrackets(jobDataRes.businessYear))
                    setPriceBrackets(priceBracketsRes);

                } catch {}
                finally {
                    setIsLoading(false)
                }
            }}
        fetchOptions();
        
    }, [jobId, customerId]);

    useEffect(() => {
        eventEmitter.emit(EventEmitterTypes.CLOSE_SIDEBAR)

        return () => {
            const wasOpen = JSON.parse(sessionStorage.getItem('sidebarWasOpen') || 'true');
            eventEmitter.emit(wasOpen ? EventEmitterTypes.OPEN_SIDEBAR : EventEmitterTypes.CLOSE_SIDEBAR);
            sessionStorage.removeItem('sidebarWasOpen');
        };
    }, []);
    const bracketTooltip = useMemo(() => {
        if (!priceBrackets?.length || !jobSquareFeet || !state?.propertyType) return null;
        const bracket = priceBrackets.find((b) =>
            jobSquareFeet >= b.minimumSquareFeet &&
            (b.maximumSquareFeet === null || jobSquareFeet <= b.maximumSquareFeet) &&
            b.propertyType === state.propertyType
        );
        if (!bracket) return null;
        const maxLabel = bracket.maximumSquareFeet != null ? bracket.maximumSquareFeet : "+";
        if (bracket.fixedPrice != null) {
            return `${bracket.minimumSquareFeet}-${maxLabel} @ $${bracket.fixedPrice} (fixed)`;
        }
        return `${bracket.minimumSquareFeet}-${maxLabel} @ $${bracket.pricePerSquareFoot}/sqft`;
    }, [jobSquareFeet, priceBrackets, state?.propertyType]);

    function resetJobData() {
        customerService.getCustomerJobByJobId(state.customerId, state.jobId).then(r => {
            reset(r)
        })
    }

    const onSubmit = data => {
        data.totalJobPrice = total;
        if(data.whenPriceApproved === '')
            data.whenPriceApproved = null
        customerService.saveJob(state.customerId, data)
            .then(r => {
                if(!r) return;
                notificationService.success("Job Saved");
                resetJobData()
            });
    }

    useEffect(() => {
        if(dateDone && dirtyFields.dateDone){
            if(jobStatusName === 'DRIVEBY' && jobStatuses){
                const todoStatus = jobStatuses.find(x => x.label === 'TOODOO');
                setValue('jobStatusId',todoStatus.value, {
                    shouldDirty: true,
                });
            }
        }
        if(!dateDone && dirtyFields.dateDone){
            if(jobStatusName === 'TOODOO' && jobStatuses){
                const drivebyStatus = jobStatuses.find(x => x.label === 'DRIVEBY');
                setValue('jobStatusId',drivebyStatus.value, {
                    shouldDirty: true,
                });
            }
        }
    }, [dateDone]);


    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.ctrlKey && e.key.toLowerCase() === 's') {
            e.preventDefault();
            onSubmit(getValues());
        }
    }, [onSubmit]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    const [showTruckModal, setShowTruckModal] = useState(false);
    const [truckModalDetails, setTruckModalDetails] = useState(null);

    function GetDateTimeFormat(arrivalDateAndTime) {
        if(!arrivalDateAndTime) return;
        const dt = new Date(arrivalDateAndTime);

        const yyyy = dt.getFullYear();
        const MM   = String(dt.getMonth() + 1).padStart(2, "0");
        const dd   = String(dt.getDate()).padStart(2, "0");
        
        const rawHour = dt.getHours();
        const hours12 = rawHour % 12 || 12; 
        const hh12    = String(hours12).padStart(2, "0");
        const mm      = String(dt.getMinutes()).padStart(2, "0");
        const ampm    = rawHour >= 12 ? "PM" : "AM";

        const time12hr = `${hh12}:${mm} ${ampm}`;
        return (
            <>
                <td>
                    {`${MM}/${dd}/${yyyy}`}
                </td>
                <td>
                    {time12hr}
                </td>
            </>
        )
    }

    const serviceStart = watch("serviceStart");
    const serviceEnd = watch("serviceEnd");

    // whenever start or end change, recalc serviceTime
    useEffect(() => {
        if (serviceStart && serviceEnd) {
            const mins = calculateMinutesDiff(serviceStart, serviceEnd);
            setValue("serviceTime", mins, { shouldDirty: true });
        }
    }, [serviceStart, serviceEnd, setValue]);

    function calculateMinutesDiff(start: string, end: string): number {
        const [startHour, startMinute] = start.split(":").map(Number);
        const [endHour, endMinute] = end.split(":").map(Number);
        const startDate = new Date(0, 0, 0, startHour, startMinute);
        const endDate = new Date(0, 0, 0, endHour, endMinute);
        let diffMin = (endDate.getTime() - startDate.getTime()) / (1000 * 60);
        if (diffMin < 0) diffMin += 24 * 60;
        return Math.round(diffMin);
    }

    return (
        <>
            <div className='customerPage'>
                {/*<!-- start customer/job header -->*/}
                <div className="customerHeader">
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="breadcrumbs mb-3">
                            <a style={{cursor: "pointer",}} onClick={() => navigate("/admin/customers")}
                               className="levelUp">
                                search results
                            </a>
                            <span className="levelDirection">
                            <i className="fa-regular fa-angle-right"></i>
                        </span>
                            <a style={{cursor: "pointer",}} onClick={() => navigate("/admin/customer-details", {
                                state: {
                                    customerId: state.customerId,
                                    customerName: state.customerName,
                                    propertyAddress: state.propertyAddress
                                }
                            })} className="levelUp">
                                customer
                            </a>

                            <span className="levelDirection">
                            <i className="fa-regular fa-angle-right"></i>
                        </span>
                            <span className="levelCurrent">
                            job
                        </span>

                        </div>
                        <div>
                            <button onClick={() => navigate("/admin/customer-details", {
                                state: {
                                    customerId: state.customerId,
                                    customerName: state.customerName,
                                    propertyAddress: state.propertyAddress
                                }
                            })} type={'button'}
                                    className="btn btn-light px-3 me-2">cancel
                            </button>
                            <button type='button'
                                    onClick={() => setShowConfirmationInvoiceModal(true)}
                                    className="btn btn-primary px-3 hvr-grow">
                                <i className="far fa-paper-plane"></i> send invoice
                            </button>
                            <button type='button' onClick={() => onSubmit(getValues())}
                                    className="btn btn-primary px-3 hvr-grow">
                                <i className="fa-regular fa-floppy-disk"></i> save
                            </button>
                        </div>
                    </div>

                    <div className="customerInfo">
                        <h1 className="levelName mb-0 d-flex align-items-center">
                            {dateDone
                                ? (() => {
                                    const [y, m, d] = dateDone.split("-").map(Number);
                                    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                    });
                                })()
                                : 'N/A'}
                            <span className={"jobBadge ms-2" + (jobStatusId === 3 ? ' infull' : '')}>
                                {jobStatusName}
                            </span>
                        </h1>
                        <h2 className="levelSecondary mb-0">
                            {state.customerName}
                        </h2>
                        <h2 className="levelSecondary mb-0">
                            {state.propertyAddress}
                        </h2>
                    </div>
                </div>
                <div className="container weirdHeader">
                    {/*<div className="row bg-white border border-light rounded p-3 shadow mb-4">
                  <div className="col-12">
                      TIMELINE pending
                  </div>
              </div>*/}
                    <div className="row">
                        {/*<!-- LEFT COL -->*/}
                        <div className="col-lg-5">
                            {/*<!-- start group STATUS -->*/}
                            <div className={"jobPageCard card shadow-sm rounded-lg" +
                                (dirtyServiceStatusCard ? " editMade" : "")
                            }>
                            <div className="card-body p-3">
                              <div className="row">
                                  <div className="col">
                                        <span className="catName">
                                            Service
                                        </span>
                                  </div>
                              </div>

                              <div className="row g-2">
                                  <div className="col-md-6 mb-3">
                                      <FormSelectCustom label={'Status'} name={'jobStatusId'} register={register} options={jobStatuses} dirtyField={dirtyFields.jobStatusId}/>
                                  </div>
                              </div>


                          </div>
                      </div>
                      {/*<!-- end group -->*/}
                      {/*<!-- start group PRICING -->*/}
                      <div className={"jobPageCard card shadow-sm rounded-lg"+
                      (dirtyPricingCard ? " editMade" : "")
                      }>
                          <div className="card-body p-3">
                              <div className="row">
                                  <div className="col">
                                        <span className="catName">
                                            Pricing
                                        </span>
                                  </div>
                              </div>

                              <div className="row g-2">
                                  <div className="col-md-3 mb-3">
                                      <label className={"form-label" +
                                          (dirtyFields.totalJobPrice ? " fieldEdit" : "")
                                      }>Estimate</label>
                                      <div className="cuteInput input-group input-group-sm">
                                          <span className="input-group-text" id="fancy">$</span>
                                          <input disabled={true} value={estimateTotal} type="text" className="form-control" placeholder="fancy" aria-label="fancy" aria-describedby="fancy"/>
                                      </div>
                                  </div>
                                  <div className="col-md-3 mb-3">
                                      <label className={"form-label"+
                                          (dirtyFields.autoAdjustmentsAmount ? " fieldEdit" : "")
                                      }>Auto Adj</label>
                                      <div className="cuteInput input-group input-group-sm">
                                          <span className="input-group-text" id="fancy">$</span>
                                          <input disabled={true} {...register("autoAdjustmentsAmount", { valueAsNumber: true })}  type="text" className="form-control" placeholder="fancy" aria-label="fancy" aria-describedby="fancy"/>
                                      </div>
                                  </div>
                                  <div className="col-md-3 mb-3">
                                      <label className={"form-label"+
                                          (dirtyFields.manualAdjustmentAmount ? " fieldEdit" : "")
                                      }>Manual Adj</label>
                                      <div className="cuteInput input-group input-group-sm">
                                          <span className="input-group-text" id="fancy">$</span>
                                          <input {...register("manualAdjustmentAmount", { valueAsNumber: true })} type="text" className="form-control" placeholder="fancy" aria-label="fancy" aria-describedby="fancy"/>
                                      </div>
                                  </div>
                              </div>
                              <div className="row text-end">
                                  <div className="col pricingFinal">
                                      <span className="pricingFinalLabel">Final Price{' '}</span>
                                      <span className="pricingFinalAmount">${Math.ceil(total)}</span>
                                  </div>
                              </div>


                          </div>
                      </div>
                      {/*<!-- end group -->*/}
                      {/*<!-- start group ESTIMATE -->*/}
                      <div className={"jobPageCard card shadow-sm rounded-lg"+
                      (dirtyEstimateCard ? " editMade" : "")
                      }>
                          <div className="card-body p-3">
                              <div className="row">
                                  <div className="col">
                                      <span className="catName">
                                            <span>Estimate</span>
                                                {!dateDone && allowEdit && <a className="truckEdit action" style={{cursor: "pointer",}} onClick={() => navigate('/admin/estimate-details', {state: {customerId: state.customerId, jobId: state.jobId, propertyAddress: state.propertyAddress, fromJobDetails: true}})}>
                                                    <i className="fa-regular fa-pencil"></i> edit
                                                </a>}
                                      </span>
                                  </div>
                              </div>

                              <div className="row g-2">
                                  <div className="col-md-6 mb-3">
                                      <label className={"form-label" +
                                          (dirtyFields.whenPriceApproved ? " fieldEdit" : "")
                                      }>Date Estimated</label>
                                      <div className="cuteInput input-group input-group-sm">
                                          <span className="input-group-text" id="fancy"><i
                                              className="fa-regular fa-calendar"></i></span>
                                          <input disabled {...register("whenPriceApproved")} type="date"
                                                 value={watch("whenPriceApproved")?.slice(0, 10) || ''}
                                                 className="form-control"
                                                 placeholder="fancy" aria-label="fancy" aria-describedby="fancy"/>
                                      </div>
                                  </div>
                                  <div className="col-md-3 mb-3">
                                      <label className={"form-label" +
                                          (dirtyFields.jobSquareFeet ? " fieldEdit" : "")
                                      }>Square Ft{'  '}{bracketTooltip && (
                                          <PopoverButton
                                              tooltipContent={bracketTooltip}
                                              iTagClass={"fa-solid fa-circle-question opacity-25"}
                                              buttonClass={"p-0"}
                                          />
                                      )}</label>
                                      <div className="cuteInput input-group input-group-sm">
                                          <input disabled={true} {...register("jobSquareFeet")} type="text"
                                                 className="form-control" placeholder="fancy" aria-label="fancy"
                                                 aria-describedby="fancy"/>
                                      </div>
                                  </div>
                                  <div className="col-md-3 mb-3">
                                      <label className={"form-label" +
                                          (dirtyFields.totalJobPrice ? " fieldEdit" : "")
                                      }>Estimate</label>
                                      <div className="cuteInput input-group input-group-sm">
                                          <span className="input-group-text" id="fancy">$</span>
                                          <input disabled={true} value={estimateTotal} type="text"
                                                 className="form-control" placeholder="fancy" aria-label="fancy"
                                                 aria-describedby="fancy"/>
                                      </div>
                                  </div>
                              </div>


                          </div>
                      </div>
                      {/*<!-- end group -->*/}
                      {/*<!-- start group TRANSACTION -->*/}
                      <div className={"jobPageCard card shadow-sm rounded-lg" +
                          (dirtyTransactionCard ? " editMade" : "")
                      }>
                          <div className="card-body p-3">
                              <div className="row">
                                  <div className="col">
                                        <span className="catName">
                                            <span>Transaction</span>
                                        </span>
                                  </div>
                              </div>

                              <div className="subCategory">
                                    <span className="subCatHeader mb-3">
                                        CREDIT CARD
                                    </span>
                                  <div className="row g-2">
                                      <div className="col-md-4 mb-3">
                                          <label className={"form-label"+
                                              (dirtyFields.cardTransactionDate ? " fieldEdit" : "")
                                          }>Trans Date</label>
                                          <div className="cuteInput input-group input-group-sm">
                                              <span className="input-group-text" id="fancy"><i className="fa-regular fa-calendar"></i></span>
                                              <input {...register("cardTransactionDate")} disabled type="date" className="form-control" placeholder="" aria-label="fancy" aria-describedby="fancy"/>
                                          </div>
                                      </div>
                                      <div className="col-md-4 mb-3">
                                          <label className={"form-label"+
                                              (dirtyFields.cardAmount ? " fieldEdit" : "")
                                          }>Amount Charged</label>
                                          <div className="cuteInput input-group input-group-sm">
                                              <span className="input-group-text" id="fancy">$</span>
                                              <input {...register("cardAmount")} disabled type="text" className="form-control" placeholder="" aria-label="fancy" aria-describedby="fancy"/>
                                          </div>
                                      </div>
                                      <div className="col-md-4 mb-3">
                                          <label htmlFor="textInput" className={"form-label"+
                                              (dirtyFields.cardTransactionNumber ? " fieldEdit" : "")
                                          }>Trans Number</label>
                                          <input {...register("cardTransactionNumber")} disabled type="text" className="form-control form-control-sm" id="textInput" aria-describedby="textInput" placeholder=""/>
                                      </div>
                                  </div>
                                  <div className="row g-2">
                                      <div className="col-md-4 mb-3">
                                          <label htmlFor="textInput" className={"form-label" +
                                              (dirtyFields.cardType ? " fieldEdit" : "")
                                          }>Card Type</label>
                                          <input {...register("cardType")} disabled type="text"
                                                 className="form-control form-control-sm" id="textInput"
                                                 aria-describedby="textInput" placeholder=""/>
                                      </div>
                                      <div className="col-md-4 mb-3">
                                          <label htmlFor="textInput" className={"form-label" +
                                              (dirtyFields.cardLast4 ? " fieldEdit" : "")
                                          }>Card Last 4</label>
                                          <input {...register("cardLast4")} disabled type="text" className="form-control form-control-sm" id="textInput" aria-describedby="textInput" placeholder=""/>
                                      </div>
                                      <div className="col-md-4 mb-3">
                                          <label htmlFor="textInput" className={"form-label"+
                                              (dirtyFields.cardExpirationYear ? " fieldEdit" : "")
                                          }>Exp MM/YYYY</label>
                                          <input value={`${cardMonth}/${cardYear}`} disabled type="text" className="form-control form-control-sm" id="textInput" aria-describedby="textInput" placeholder=""/>
                                      </div>
                                  </div>
                              </div>
                              <div className="subCategory">
                                    <span className="subCatHeader mb-3">
                                        Deposit
                                    </span>
                                  <div className="row g-2">
                                      <div className="col-md-4 mb-3">
                                          <label className={"form-label"+
                                              (dirtyFields.depositSlipDate ? " fieldEdit" : "")
                                          }>Deposit Date</label>
                                          <div className="cuteInput input-group input-group-sm">
                                              <span className="input-group-text" id="fancy"><i className="fa-regular fa-calendar"></i></span>
                                              <input {...register("depositSlipDate")} type="date" className="form-control" placeholder="fancy" aria-label="fancy" aria-describedby="fancy"/>
                                          </div>
                                      </div>
                                      <div className="col-md-4 mb-3">
                                          <label className={"form-label"+
                                              (dirtyFields.depositAmount ? " fieldEdit" : "")
                                          }>Amount Received</label>
                                          <div className="cuteInput input-group input-group-sm">
                                              <span className="input-group-text" id="fancy">$</span>
                                              <input {...register("depositAmount")} type="text" className="form-control" placeholder="fancy" aria-label="fancy" aria-describedby="fancy"/>
                                          </div>
                                      </div>
                                      <div className="col-md-4 mb-3">
                                          <label htmlFor="textInput" className={"form-label" +
                                              (dirtyFields.depositSlipNumber ? " fieldEdit" : "")
                                          }>Deposit Slip</label>
                                          <input {...register("depositSlipNumber")} type="text"
                                                 className="form-control form-control-sm" id="textInput"
                                                 aria-describedby="textInput" placeholder="text"/>
                                      </div>
                                  </div>
                              </div>
                          </div>
                      </div>
                            {/*<!-- end group -->*/}
                        </div>
                        {/*<!-- RIGHT COL -->*/}
                        <div className="col-lg-7">
                            {/*<!-- start group SERVICE -->*/}
                            <div className={"jobPageCard card shadow-sm rounded-lg"+
                      (dirtyServiceOtherCard ? " editMade" : "")
                      }>
                          <div className="card-body p-3">
                              <div className="row">
                                  <div className="col">
                                        <span className="catName">
                                            <span>Service</span>
                                        </span>
                                  </div>
                              </div>
                              <div className="row g-2">
                                  <div className="col">
                                      <div className="serviceInputTable">
                                          <table className="table table-borderless">
                                              <tbody>
                                              <tr>
                                                  <td>
                                                      <label className={"form-label"+
                                                          (dirtyFields.dateDone ? " fieldEdit" : "")
                                                      }>Date Done</label>
                                                      <div className="cuteInput input-group input-group-sm">
                                                          <AvailableDatePicker
                                                              name="dateDone"
                                                              control={control}
                                                              errors={errors}
                                                              colorUnavailableDates
                                                              availableDates={availableDates?.map(dateStr => {
                                                                  const [year, month, day] = dateStr.split("-").map(Number);
                                                                  return new Date(year, month - 1, day); // Local midnight
                                                              })}
                                                              className={'form-control form-control-sm'}
                                                          />
                                                      </div>
                                                  </td>
                                                  <td>
                                                      <label htmlFor="textInput" className={"form-label"+
                                                          (dirtyFields.serviceTime ? " fieldEdit" : "")
                                                      }>Service Time</label>
                                                      <input {...register("serviceTime")} type="number" className="form-control form-control-sm" id="textInput" aria-describedby="textInput" placeholder="text"/>
                                                  </td>
                                                  <td>
                                                      <label htmlFor="textInput" className={"form-label"+
                                                          (dirtyFields.serviceStart ? " fieldEdit" : "")
                                                      }>Service Start</label>
                                                      <input {...register("serviceStart")} type="time" className="form-control form-control-sm" id="textInput" aria-describedby="textInput" placeholder="text"/>
                                                  </td>
                                                  <td>
                                                      <label htmlFor="textInput" className={"form-label"+
                                                          (dirtyFields.serviceEnd ? " fieldEdit" : "")
                                                      }>Service End</label>
                                                      <input {...register("serviceEnd")} type="time" className="form-control form-control-sm" id="textInput" aria-describedby="textInput" placeholder="text"/>
                                                  </td>
                                              </tr>
                                              </tbody>
                                          </table>
                                      </div>
                                  </div>
                              </div>
                          </div>
                      </div>
                      {/*<!-- end group -->*/}
                      {/*<!-- start group TRUCKS -->*/}
                      <div className={"jobPageCard card shadow-sm rounded-lg"+
                      (dirtyTrucksCard ? " editMade" : "")
                      }>
                          <div className="card-body p-3">
                              <div className="row">
                                  <div className="col">
                                        <span className="catName">
                                            <span>Trucks</span>
                                            <a className="truckEdit action" style={{cursor: "pointer",}} onClick={() => {
                                                setTruckModalDetails(null)
                                                setShowTruckModal(true);
                                            }}><i className="fa-regular fa-plus"></i> truck</a>
                                        </span>
                                  </div>
                              </div>
                              <div className="row mb-3">
                                  <div className="col">
                                      <div className="cardTable">
                                          <table className="table table-sm table-hover align-middle">
                                              <thead>
                                              <tr>
                                                  <th>Truck</th>
                                                  <th>Arrival Window</th>
                                                  <th>Date</th>
                                                  <th>Time</th>
                                                  <th className="text-end">Actions</th>
                                              </tr>
                                              </thead>
                                              <tbody>
                                              {jobTrucks?.map((truck, i) => {
                                                  return (
                                                  <tr>
                                                      <td>
                                                          #{truck.truckNumber}
                                                      </td>
                                                      <td>
                                                          {truck.arrivalTimeWindowId === 1 ? 'Pending' : truck.arrivalTimeWindowId === 2 ? 'Morning' : 'Afternoon'}
                                                      </td>
                                                      {GetDateTimeFormat(truck.arrivalDateAndTime)}
                                                      <td className="text-end">
                                                          <a style={{cursor: 'pointer'}} className="truckEdit" onClick={() => {
                                                              setTruckModalDetails(truck);
                                                              setShowTruckModal(true);
                                                          }}>edit</a>
                                                      </td>
                                                  </tr>
                                                  )
                                              })}

                                              </tbody>
                                          </table>
                                      </div>
                                  </div>
                              </div>
                              <div className="row g-2">
                                  <div className="col-12 mb-3">
                                      <label htmlFor="textArea" className={"form-label" +
                                          (dirtyFields.truckComments ? " fieldEdit" : "")
                                      }>Truck Comments</label>
                                      <textarea {...register("truckComments")} className="form-control form-control-sm"
                                                id="textArea" rows={3}></textarea>
                                  </div>
                              </div>
                          </div>
                      </div>
                      {/*<!-- end group -->*/}
                      {/*<!-- start group COMMENTS -->*/}
                      {/*<!-- editMade class -->*/}
                      <div className={"jobPageCard card shadow-sm rounded-lg"+
                          (dirtyCommentsCard ? " editMade" : "")
                      }>
                          <div className="card-body p-3">
                              <div className="row">
                                  <div className="col">
                                        <span className="catName">
                                            Comments
                                        </span>
                                  </div>
                              </div>
                              <div className="row g-2">
                                  <div className="col-md-6 mb-3">
                                      <label htmlFor="textArea" className={"form-label" +
                                          (dirtyFields.officeComments ? " fieldEdit" : "")
                                      }>Office Comments</label>
                                      <textarea {...register("officeComments")} className="form-control form-control-sm"
                                                id="textArea" rows={3}></textarea>
                                  </div>
                                  <div className="col-md-6 mb-3">
                                      <label htmlFor="textArea" className={"form-label" +
                                          (dirtyFields.webComments ? " fieldEdit" : "")
                                      }>Web Comments</label>
                                      <textarea {...register("webComments")} className="form-control form-control-sm"
                                                id="textArea" rows={3}></textarea>
                                  </div>
                              </div>
                              <div className="row g-2">
                                  <div className="col-md-6 mb-3">
                                      <label htmlFor="textArea" className={"form-label" +
                                          (dirtyFields.persistentOfficeComments ? " fieldEdit" : "")
                                      }>Persistent Office Comments</label>
                                      <textarea {...register("persistentOfficeComments")} className="form-control form-control-sm"
                                                id="textArea" rows={3}></textarea>
                                  </div>
                              </div>
                          </div>
                      </div>
                      {/*<!-- end group -->*/}
                  </div>
                    </div>
                </div>
            </div>
            <NewEditTruckModal show={showTruckModal} toggle={() => setShowTruckModal(false)} truck={truckModalDetails}
                               refreshData={async () => setJobTrucks(await customerService.getTrucksByJobId(state.customerId, state.jobId))}
                               customerId={state.customerId} jobId={state.jobId}/>
            <ConfirmationModal isDanger={false} confirmButtonText={'Yes, send to customer'} cardText={'Are you sure you want to send an invoice'} show={showConfimationModalInvoice} toggle={() => setShowConfirmationInvoiceModal(false)} onConfirm={() => {
                customerService.sendInvoiceEmail(state.customerId, state.jobId, null).then(r => {
                    if(!r) return
                    notificationService.success('Invoice message sent')
                })
                }
            }/>

        </>
    );
}

export default JobDetailsPage